import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { BiEdit } from "react-icons/bi";
import { FaLayerGroup, FaLink, FaPalette, FaSitemap, FaUser } from "react-icons/fa";
import { FaUserLarge, FaUsersGear } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { AuditObjectTypes, RoleRequestParams, RoleResponseModel, UserModel } from '../../_core/_model.ts';
import { DeleteRole, GetRoles } from '../../_core/_request.ts';
import { LabelConstants } from '../../Constants.ts';
import { useAppContext } from '../../context/AppContext.tsx';
import { AuthContext } from '../../context/AuthContext.js';
import { defaultTheme } from '../../styles/themes/defaultTheme';
import CreateButton from '../../UIComponents/CreateButton.tsx';
import DeleteRowButton from '../../UIComponents/DeleteRowButton.tsx';
import { AreObjectsEquals } from '../../Utilities/Utilities.ts';
import ComponentLayout from '../Layouts/ComponentLayout.tsx';
import RoleGroupEditor from '../Roles/RoleGroupEditor.tsx';
import TooltipButton from '../Tools/TooltipButton.tsx';
import RoleDetailsOnTitle from "../Roles/RoleDetailsOnTitle.tsx"
import SearchAndAdvancedFilters, { appliedFilterType } from './SearchAndAdvancedFilters.tsx';
import TableLayoutEx from './TableLayoutEx.tsx';
import TableSummary from './TableSummary.tsx';
import { useToast } from '../../context/ToastContext.tsx';

const columnVisibility = {
    roleID: true,
    roleName: true,
    description: true,
    dataMapCode: true
};

export default function RolesTable() {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { dimensions, refreshRoles, allUsers, allRoleGroups, roles } = useAppContext();
    const [createClicked, setCreateClicked] = useState(false);
    const [rows, setRows] = useState<RoleResponseModel[] | null>(null);
    const [rowSelection, setRowSelection] = React.useState<any[]>([]);
    const defaultFilter = {
        roleID: null, roleName: "", roleCode: "", dataMapColumn: null
        , roleGroupID: null, userID: null, typeOfRoles: "all"
    };
    const [selectedFilter, setSelectedFilter] = useState<RoleRequestParams>(defaultFilter);

    const { user: loginUser } = useContext(AuthContext);

    const initRows = () => {
        setRows(null);
        refreshRoles([], (r) => {
            getRows(defaultFilter, undefined)
        });
    }

    const getRows = (request: RoleRequestParams, afterLoad: any) => {
        GetRoles(request).then((x) => {
            setSelectedFilter(request);
            setRows([...x.data]);
            if (typeof afterLoad != 'undefined')
                afterLoad(x.data);
        })
    }

    useEffect(() => {
        getRows(defaultFilter, undefined)
    }, [])

    const minColumn = {
        "roleID": {
            accessorKey: 'roleID',
            size: 30,
            enableSorting: false,
            enableResizing: false,
            header: ({ table }) => (
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={table.getIsAllPageRowsSelected()}
                    //indeterminate={table.getIsSomePageRowsSelected()}
                    onChange={table.getToggleAllPageRowsSelectedHandler()}
                />
            ),
            cell: ({ row }) => (
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
        },
        "Name": {
            accessorKey: 'roleName',
            enableSorting: true,
            enableResizing: false,
            header: 'Name',
            cell: ({ cell, row }) => cell.getValue()
        }
    };

    const columns = {
        "roleID": {
            accessorKey: 'roleID',
            size: 35,
            enableSorting: false,
            enableResizing: false,
            header: ({ table }) => (
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={table.getIsAllPageRowsSelected()}
                    //indeterminate={table.getIsSomePageRowsSelected()}
                    onChange={table.getToggleAllPageRowsSelectedHandler()}
                />
            ),
            cell: ({ row }) => (
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
        },
        "Name": {
            accessorKey: 'roleName',
            header: 'Name',
            enableSorting: true,
            enableResizing: true,
            cell: ({ cell, row }) => (
                <TooltipButton maxWidth={750} title={() =>
                    row.getAllCells()[0].getValue() == null ? (
                        ""
                    ) : (
                        <RoleDetailsOnTitle roleId={row.getAllCells()[0].getValue().toString()} />
                    )
                }>
                    {cell.getValue()}
                </TooltipButton>
            )
        },
        "Description": {
            accessorKey: 'description',
            header: 'Description',
            enableSorting: true,
            enableResizing: true,
            cell: ({ cell, row }) => cell.getValue()
        },
        "Dimension": {
            id: 'Dimension',
            accessorKey: "dataMapCode",
            size: 200,
            header: "Dimension",
            enableResizing: false,
            enableSorting: true,
            cell: (cell) => {
                let dCell = cell.getValue();
                return <span style={{
                    padding: "3.5px 7px",
                    fontSize: "12.25px",
                    fontWeight: "400",
                    borderRadius: "16px",
                    backgroundColor: dimensions.find((d) => d.dimensionCode == dCell)?.bg,
                    color: '#6c757d',
                    border: `1px solid ${dimensions.find((d) => d.dimensionCode == dCell) ? dimensions.find((d) => d.dimensionCode == dCell)?.color : '#ced4da'}`
                }}>{dimensions.find((x) => x.dimensionCode == dCell)?.dimensionName}</span>
            },
            sortFn: (a, b) => {
                let namea = dimensions.find((x) => x.dimensionCode == a.original.dataMapCode)?.dimensionName;
                let nameb = dimensions.find((x) => x.dimensionCode == b.original.dataMapCode)?.dimensionName;
                if (typeof namea != 'undefined' && typeof nameb != 'undefined')
                    return namea.localeCompare(nameb);
                return false;
            }
        },
        "action":
        {
            accessorKey: '',
            header: ' ',
            size: 80,
            enableSorting: false,
            enableResizing: false,
            cell: ({ cell, row }) => (
                <div className='d-flex flex-row align-items-center gap-1'>
                    <TooltipButton title={`Edit Role`}>
                        <button className='btn btn-outline-primary py-1 px-2 m-0 border-0' onClick={(e) => {
                            e.stopPropagation(); // Prevents row selection
                            navigate("/roles/roledetails?roleId=" + row.getAllCells()[0].getValue());
                        }}><BiEdit size={20} /></button>
                    </TooltipButton>
                    <DeleteRowButton {...{
                        cell: cell, row: row, deleteAPI: (r) => {
                            DeleteRole(row.getAllCells()[0].getValue(), loginUser.userID).then((x) => {
                                if (x.data != "") {
                                    showToast({
                                        message: "Failed to Delete Role " + row.getAllCells()[0].getValue() + " with error: " + x.data,
                                        type: "warning",
                                        duration: 5000
                                    })
                                } else {
                                    initRows();
                                }
                            });

                        }, nameFieldId: "roleName", title: "Role"
                    }} />
                </div>
            )
        }
    };

    return (
        <ComponentLayout exButtons={undefined} instanceId={0} objectType={AuditObjectTypes.Role} title='Roles' bodyHeight={400}
            subHeader={
                <div>
                    <SearchAndAdvancedFilters {...{
                        initialValues: { "": null },
                        exButton: <div className='d-flex flex-row align-items-center gap-2 overflow-visible text-nowrap' >
                            <AnimatePresence>
                                {
                                    Object.keys(rowSelection).length > 0 &&
                                    <motion.button
                                        onClick={() => {
                                            setCreateClicked(true);
                                        }}
                                        className='btn'
                                        style={{
                                            background: defaultTheme.colors.accent,
                                            color: 'white',
                                            padding: "7px 10px",
                                            height: "34px"
                                        }}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}>
                                        <div className='d-flex flex-row align-items-center gap-2' >
                                            <FaLayerGroup />
                                            Create a {LabelConstants.RoleGroups}
                                        </div>
                                    </motion.button>
                                }
                            </AnimatePresence>
                            <CreateButton {...{
                                title: "Click to create a new role",
                                label: "Create a role",
                                onClick: () => {
                                    navigate("/roles/roledetails")
                                }
                            }} />
                        </ div>,
                        onChange: (filters, afterLoad) => {
                            getRows({
                                roleID: null, roleName: filters.name, roleCode: "", dataMapColumn: filters.dataMapColumn,
                                roleGroupID: filters.roleGroupID, userID: filters.userID, typeOfRoles: (filters.typeOfRoles == null) ? "all" : filters.typeOfRoles
                            }, afterLoad);
                        },
                        filters: {
                            "dataMapColumn": {
                                exValues: [],
                                filterType: appliedFilterType.Dimensions,
                                icon: <FaPalette />,
                                label: "Dimensions"
                            },
                            "roleGroupID": {
                                exValues: [],
                                filterType: appliedFilterType.RoleGroup,
                                icon: <FaSitemap />,
                                label: LabelConstants.RoleGroups
                            },
                            "userID": {
                                exValues: [],
                                filterType: appliedFilterType.User,
                                appliedFilter: (u: UserModel) => u.userID != -1,
                                icon: <FaUserLarge />,
                                label: "Users"
                            },
                            "typeOfRoles": {
                                exValues: [
                                    { key: "all", value: "All Roles" },
                                    { key: "assigned", value: "Assigned" },
                                    { key: "unassigned", value: "Unassigned" }
                                ],
                                filterType: appliedFilterType.SingleDropdown,
                                icon: <FaLink size={16} />,
                                label: "Assignment Status"
                            }
                        },
                        values: {
                            "Dimensions": null,
                            "roleID": null,
                            "roleGroupID": null,
                            "userID": null,
                            "appliationAccess": null,
                            "typeOfRoles": "all"
                        },
                    }} />
                    {/* <GroupFilter groups={roleGroups} onChange={() => { }} selected={selectedGroup} /> */}
                    <TableSummary {...{
                        total: roles?.length, filterred: rows?.length, name: "Role",
                        applyiedFilter: (!AreObjectsEquals(selectedFilter, defaultFilter))
                    }} />
                </div>
            } cBody={
                <div className='bg-theme-card h-100' style={{ display: 'flex', width: '100%' }}>
                    <div style={{ width: createClicked ? '20%' : '100%', transition: 'all 0.3s ease' }}>
                        <TableLayoutEx expandedUI={undefined} needRowSelection={true} onRowSelection={setRowSelection} rows={rows == null ? [] : rows} objType={AuditObjectTypes.Role}
                            columnDefinition={(createClicked) ? minColumn : columns} handleRowDoubleClick={(row) => {
                                navigate("/roles/roledetails?roleId=" + row.original.roleID);
                            }} needSelection={true} loading={rows == null} isSelected={(row) => {
                                return row.getIsSelected();
                            }} emptyMessage={
                                <AnimatePresence>
                                    <motion.div
                                        className="d-flex flex-column align-items-center justify-content-center text-muted-theme p-5 m-4 bg-gray"
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5, ease: "easeOut" }}
                                        style={{
                                            border: "2px dashed #dee2e6",
                                            borderRadius: "10px",
                                            marginTop: "40px",
                                        }}
                                    >
                                        <FaUsersGear size={60} color="#adb5bd" className="mb-3" />
                                        <h5 className="fw-semibold">No Role Found</h5>
                                        {
                                            (!AreObjectsEquals(selectedFilter, defaultFilter)) ?
                                                <p className="text-center" style={{ maxWidth: 400 }}>
                                                    No Role found for selected filter.
                                                </p> :
                                                <p className="text-center" style={{ maxWidth: 400 }}>
                                                    It looks like there are no records yet. Click{" "}
                                                    <strong>“Create”</strong> to get started.
                                                </p>
                                        }
                                    </motion.div>
                                </AnimatePresence>
                            } setSelected={(row) => {
                                row.toggleSelected();
                                // navigate("/roles/roledetails?roleId=" + row.getValue('roleID'))
                                // let pVal = selectedRows;
                                // let pIndx = pVal.indexOf(row.original.roleID);
                                // if (pIndx > -1) {
                                //     pVal.splice(pIndx, 1);
                                // }
                                // else
                                //     pVal.push(row.original.roleID);
                                // setSelectedRows([...pVal]);
                            }} />
                    </div>
                    {
                        createClicked && (
                            <div style={{ width: '80%', borderLeft: '1px solid #ddd', background: '#fff' }}>
                                <RoleGroupEditor group={{
                                    roleGroupID: 0,
                                    roleGroupName: "",
                                    roleGroupDescription: "",
                                    roleIDS: rowSelection.map((row) => row.roleID).join(","),
                                    createdBy: loginUser.userID,
                                    createdAt: null
                                }} onClose={(refresh) => {
                                    if (refresh) {
                                        initRows();
                                        setRowSelection([]);
                                    }
                                    setCreateClicked(false);
                                }} fromGroup={false} />
                            </div>
                        )
                    }
                </div >
            } />
    );
}
